import { Link, router } from '@inertiajs/react';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toastFlash } from '@/lib/flash-toast';
import type { TrackAdmin } from '@/types';
import { EditTrackDialog } from './EditTrackDialog';

interface TracksTableProps {
  eventSlug: string;
  tracks: TrackAdmin[];
}

export function TracksTable({ eventSlug, tracks }: TracksTableProps) {
  const [isPending, setIsPending] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const editTrack = tracks.find((track) => track.id === editId) ?? null;

  const handleDelete = () => {
    if (!deleteId) return;

    setIsPending(true);
    router.delete(`/admin/${eventSlug}/tracks/${deleteId}`, {
      preserveScroll: true,
      onSuccess: (page) => toastFlash(page, 'Track deleted successfully'),
      onError: () =>
        toast({ type: 'error', description: 'Failed to delete track' }),
      onFinish: () => {
        setIsPending(false);
        setDeleteId(null);
      },
    });
  };

  if (tracks.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>No tracks found. Create one to get started.</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tracks.map((track) => (
              <TableRow key={track.id}>
                <TableCell>
                  <Link
                    href={`/admin/${eventSlug}/tracks/${track.slug}`}
                    className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {track.name}
                  </Link>
                </TableCell>
                <TableCell>
                  {track.description || (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <Link href={`/admin/${eventSlug}/tracks/${track.id}`}>
                        View
                      </Link>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditId(track.id)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteId(track.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Track</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this track? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {editTrack && (
        <EditTrackDialog
          key={editTrack.id}
          eventSlug={eventSlug}
          track={editTrack}
          onClose={() => setEditId(null)}
        />
      )}
    </>
  );
}
